/**
 * Collection Sorting and Filtering
 * Handles product sorting, filtering, and view toggling on collection pages
 */

class CollectionHandler {
  constructor() {
    this.products = [];
    this.filteredProducts = [];
    this.activeFilters = {
      make: [],
      condition: [],
      price: [],
      category: []
    };
    this.currentSort = 'manual';
    this.currentView = 'grid';
    this.currentColumns = 4;
    this.currentPerPage = 20;
    this.searchTerm = ''; // Initialize search term

    // Prevent initial flash of all products before JS pagination/filtering runs
    this.isInitialRender = true;

    this.init();
  }

  init() {
    // Cache DOM elements
    this.sortSelect = document.querySelector('[data-sort-by]');
    this.productsContainer = document.querySelector('[data-products-container]');
    this.productItems = document.querySelectorAll('[data-product-item]');
    this.paginationList = document.querySelector('[data-pagination-list]');
    this.productCount = document.querySelector('[data-product-count]');
    this.noResults = document.querySelector('[data-no-results]');
    this.activeFiltersContainer = document.querySelector('[data-active-filters]');
    this.activeFiltersList = document.querySelector('[data-active-filters-list]');

    // Hide products container until first pagination completes to avoid flash
    if (this.productsContainer) {
      this.productsContainer.style.opacity = '0';
      this.productsContainer.style.transition = 'opacity 0.2s ease';
    }
           
           // Search input - scope to collection page only (not header)
           const collectionPage = document.querySelector('.collection-page');
           this.searchInput = collectionPage ? collectionPage.querySelector('[data-search-input]') : null;
           
           // Column controls
           this.columnBtns = document.querySelectorAll('[data-columns]');
           
           // Products per page
           this.perPageSelect = document.querySelector('[data-per-page]');
           
           // View toggle buttons
           this.viewToggleBtns = document.querySelectorAll('[data-view]');
           
           // Filter elements
           this.filterInputs = document.querySelectorAll('[data-filter]');
           this.clearFiltersBtn = document.querySelectorAll('[data-clear-filters]');
           this.filtersToggle = document.querySelector('[data-filters-toggle]');
           this.filtersOverlay = document.querySelector('[data-filters-overlay]');
           this.filtersBackdrop = document.querySelector('[data-filters-backdrop]');
           this.filtersClose = document.querySelector('[data-filters-close]');
           this.applyFiltersBtn = document.querySelector('[data-apply-filters]');
    
    if (!this.productsContainer) return;
    
    // Store original products data
    this.storeProducts();
    
    // Set up event listeners
    this.setupEventListeners();
    
    // Set initial per page from URL (before sorting)
    this.setPerPageFromURL();
    
    // CRITICAL: Set initial sort from URL and apply it
    // This must happen after products are stored
    // Use a small delay to ensure products are fully stored
    setTimeout(() => {
      this.setSortFromURL();
      
      // Also force apply sort as backup (handles edge cases)
      setTimeout(() => {
        this.forceApplySort();
      }, 150);
    }, 50);
    
    // Note: setSortFromURL() will call sortProducts() which applies pagination
    // So we don't need to call applyPagination() separately here
    
    // Listen for browser back/forward navigation
    window.addEventListener('popstate', () => {
      // Re-read sort from URL and re-apply
      setTimeout(() => {
        this.setSortFromURL();
      }, 50);
    });
  }

  storeProducts() {
    // CRITICAL: Make sure we get ALL products from the container, not just visible ones
    // First, ensure all products are visible in DOM (remove any server-side pagination hiding)
    if (this.productsContainer) {
      const allProductElements = this.productsContainer.querySelectorAll('[data-product-item]');
      allProductElements.forEach(item => {
        // Ensure all products are visible in DOM for sorting
        item.style.display = '';
        item.style.visibility = '';
        item.style.opacity = '';
      });
      
      // Update productItems to include ALL products
      this.productItems = Array.from(allProductElements);
    }
    
    // Store ALL products (not just current page)
    this.products = Array.from(this.productItems).map((item, index) => ({
      element: item,
      title: item.querySelector('.product-card__title')?.textContent.trim() || '',
      price: parseFloat(item.dataset.productPrice) || 0,
      tags: item.dataset.productTags ? item.dataset.productTags.split(',') : [],
      titleLower: (item.dataset.productTitle || item.querySelector('.product-card__title')?.textContent.trim() || '').toLowerCase(),
      originalIndex: index
    }));
    
    // CRITICAL: filteredProducts should start with ALL products
    this.filteredProducts = [...this.products];
  }

  setupEventListeners() {
    // Sort select
    if (this.sortSelect) {
      this.sortSelect.addEventListener('change', (e) => {
        this.currentSort = e.target.value;
        this.sortProducts();
        this.updateURL();
      });
    }
    
    // View toggle
    this.viewToggleBtns.forEach(btn => {
      btn.addEventListener('click', (e) => {
        const view = btn.dataset.view;
        this.changeView(view);
      });
    });
    
    // Filter inputs
    this.filterInputs.forEach(input => {
      input.addEventListener('change', () => {
        this.applyFilters();
      });
    });
    
    // Clear filters buttons
    this.clearFiltersBtn.forEach(btn => {
      btn.addEventListener('click', () => {
        this.clearAllFilters();
      });
    });
    
    // Search input
    if (this.searchInput) {
      this.searchInput.addEventListener('input', this.debounce((e) => {
        this.handleSearch(e.target.value);
      }, 300));
    }
    
    // Column controls
    this.columnBtns.forEach(btn => {
      btn.addEventListener('click', (e) => {
        this.handleColumnChange(e.target.dataset.columns);
      });
    });
    
    // Products per page
    if (this.perPageSelect) {
      this.perPageSelect.addEventListener('change', (e) => {
        this.handlePerPageChange(e.target.value);
      });
    }
    
    // Mobile filters
    if (this.filtersToggle) {
      this.filtersToggle.addEventListener('click', () => {
        this.openMobileFilters();
      });
    }
    
    if (this.filtersClose || this.filtersBackdrop) {
      [this.filtersClose, this.filtersBackdrop].forEach(el => {
        if (el) {
          el.addEventListener('click', () => {
            this.closeMobileFilters();
          });
        }
      });
    }
    
    if (this.applyFiltersBtn) {
      this.applyFiltersBtn.addEventListener('click', () => {
        this.closeMobileFilters();
      });
    }
  }

  setSortFromURL() {
    const urlParams = new URLSearchParams(window.location.search);
    const sortParam = urlParams.get('sort_by');
    
    // CRITICAL: Always set sort value from URL or dropdown, then apply sorting
    if (sortParam && this.sortSelect) {
      this.sortSelect.value = sortParam;
      this.currentSort = sortParam;
    } else if (this.sortSelect) {
      // If no sort in URL, use dropdown value or default
      this.currentSort = this.sortSelect.value || 'manual';
    } else {
      this.currentSort = 'manual';
    }
    
    // CRITICAL: Always apply sorting after setting sort value
    // This ensures products are sorted on page load, especially when navigating between pages
    this.applySortWithRetry();
  }

  applySortWithRetry(retryCount = 0) {
    const maxRetries = 10; // Try up to 10 times (1 second total)
    
    if (this.filteredProducts.length > 0) {
      // Products are ready, apply sorting immediately
      this.sortProducts();
    } else if (retryCount < maxRetries) {
      // Products not ready yet, retry after delay
      setTimeout(() => {
        this.applySortWithRetry(retryCount + 1);
      }, 100);
    } else {
      // Max retries reached, try one more time with stored products
      if (this.products.length > 0) {
        this.filteredProducts = [...this.products];
        this.sortProducts();
      } else {
        // Last resort: re-store products and try again
        this.storeProducts();
        if (this.filteredProducts.length > 0) {
          this.sortProducts();
        }
      }
    }
  }

  forceApplySort() {
    // Force apply sort - useful when page loads with sort parameter
    // This ensures sort is applied even if timing is off
    if (this.filteredProducts.length === 0 && this.products.length > 0) {
      this.filteredProducts = [...this.products];
    }
    
    if (this.filteredProducts.length > 0) {
      // Get sort from URL or dropdown
      const urlParams = new URLSearchParams(window.location.search);
      const sortParam = urlParams.get('sort_by');
      
      if (sortParam && this.sortSelect) {
        this.sortSelect.value = sortParam;
        this.currentSort = sortParam;
      } else if (this.sortSelect) {
        this.currentSort = this.sortSelect.value || 'manual';
      }
      
      // Force apply sorting
      this.sortProducts();
    }
  }

  setPerPageFromURL() {
    const urlParams = new URLSearchParams(window.location.search);
    const limit = urlParams.get('limit');
    if (limit && this.perPageSelect) {
      this.perPageSelect.value = limit;
      this.currentPerPage = parseInt(limit, 10) || 50;
    } else {
      this.currentPerPage = 50;
    }
  }

  sortProducts() {
    // Server-side pagination: server already sent this page in the correct sort order.
    // Skip client-side reorder so display matches server exactly (no flash, full compatibility).
    const isSinglePageFromServer = this.filteredProducts.length <= this.currentPerPage;
    if (isSinglePageFromServer) {
      this.applyPagination();
      return;
    }

    // Client-side: multiple pages in DOM, sort and reorder
    const sortedProducts = [...this.filteredProducts];
    
    switch (this.currentSort) {
      case 'title-ascending':
        sortedProducts.sort((a, b) => {
          const titleA = a.titleLower || a.title.toLowerCase();
          const titleB = b.titleLower || b.title.toLowerCase();
          if (titleA < titleB) return -1;
          if (titleA > titleB) return 1;
          return 0;
        });
        break;
      
      case 'title-descending':
        sortedProducts.sort((a, b) => {
          const titleA = a.titleLower || a.title.toLowerCase();
          const titleB = b.titleLower || b.title.toLowerCase();
          if (titleA > titleB) return -1;
          if (titleA < titleB) return 1;
          return 0;
        });
        break;
      
      case 'price-ascending':
        sortedProducts.sort((a, b) => {
          // If prices are equal, sort by title for stable sort
          if (a.price === b.price) {
            const titleA = a.titleLower || a.title.toLowerCase();
            const titleB = b.titleLower || b.title.toLowerCase();
            if (titleA < titleB) return -1;
            if (titleA > titleB) return 1;
            return 0;
          }
          return a.price - b.price;
        });
        break;
      
      case 'price-descending':
        sortedProducts.sort((a, b) => {
          // If prices are equal, sort by title for stable sort
          if (a.price === b.price) {
            const titleA = a.titleLower || a.title.toLowerCase();
            const titleB = b.titleLower || b.title.toLowerCase();
            if (titleA < titleB) return -1;
            if (titleA > titleB) return 1;
            return 0;
          }
          return b.price - a.price;
        });
        break;
      
      case 'created-descending':
        // Newest first (reverse original order)
        sortedProducts.sort((a, b) => b.originalIndex - a.originalIndex);
        break;
      
      case 'created-ascending':
        // Oldest first (original order)
        sortedProducts.sort((a, b) => a.originalIndex - b.originalIndex);
        break;
      
      case 'best-selling':
      case 'manual':
      default:
        // Keep original order for featured/best-selling
        sortedProducts.sort((a, b) => a.originalIndex - b.originalIndex);
        break;
    }
    
    // CRITICAL: Reorder DOM elements to match sorted array order
    // This ensures products appear in the correct sorted order in the DOM
    if (this.productsContainer && sortedProducts.length > 0) {
      // Store original filtered count
      const originalFilteredCount = sortedProducts.length;
      
      // Get all products (including non-filtered ones that should stay hidden)
      const allProductsInContainer = Array.from(this.productsContainer.querySelectorAll('[data-product-item]'));
      const nonFilteredProducts = allProductsInContainer.filter(item => {
        return !sortedProducts.some(p => p.element === item);
      });
      
      // Remove ALL products from DOM temporarily
      allProductsInContainer.forEach(item => {
        if (item.parentNode === this.productsContainer) {
          this.productsContainer.removeChild(item);
        }
      });
      
      // Re-append sorted filtered products first (in correct order)
      const fragment = document.createDocumentFragment();
      sortedProducts.forEach(product => {
        fragment.appendChild(product.element);
      });
      this.productsContainer.appendChild(fragment);
      
      // Then append non-filtered products (these will be hidden by pagination)
      nonFilteredProducts.forEach(item => {
        this.productsContainer.appendChild(item);
      });
      
      // Re-query to sync arrays with DOM order
      const allProductElements = this.productsContainer.querySelectorAll('[data-product-item]');
      this.productItems = Array.from(allProductElements);
      
      // Rebuild filteredProducts from reordered DOM (first N products are the sorted filtered ones)
      this.filteredProducts = sortedProducts;
    }
    
    // Apply pagination after sorting ALL products
    this.applyPagination();
  }

  applyFilters() {
    // Collect active filters
    this.activeFilters = {
      make: [],
      condition: [],
      price: [],
      category: []
    };
    
    this.filterInputs.forEach(input => {
      if (input.checked) {
        const filterType = input.dataset.filter;
        if (this.activeFilters.hasOwnProperty(filterType)) {
          this.activeFilters[filterType].push(input.value);
        }
      }
    });
    
    // Filter products
    this.filteredProducts = this.products.filter(product => {
      // Check search term (only if search term exists and is not empty)
      if (this.searchTerm && this.searchTerm.trim().length > 0) {
        const searchableText = `${product.title} ${product.tags.join(' ')}`.toLowerCase();
        if (!searchableText.includes(this.searchTerm.toLowerCase())) {
          return false;
        }
      }
      
      // Check make filter
      if (this.activeFilters.make.length > 0) {
        const hasMake = this.activeFilters.make.some(make => 
          product.tags.includes(`make-${make}`)
        );
        if (!hasMake) return false;
      }
      
      // Check condition filter
      if (this.activeFilters.condition.length > 0) {
        const hasCondition = this.activeFilters.condition.some(condition => 
          product.tags.includes(condition)
        );
        if (!hasCondition) return false;
      }
      
      // Check category filter
      if (this.activeFilters.category.length > 0) {
        const hasCategory = this.activeFilters.category.some(category => 
          product.tags.includes(category)
        );
        if (!hasCategory) return false;
      }
      
      // Check price filter
      if (this.activeFilters.price.length > 0) {
        const inPriceRange = this.activeFilters.price.some(range => {
          if (range === '500+') {
            return product.price >= 50000; // $500 in cents
          }
          
          const [min, max] = range.split('-').map(v => parseFloat(v) * 100);
          return product.price >= min && product.price <= max;
        });
        if (!inPriceRange) return false;
      }
      
      return true;
    });
    
    // Update active filters display
    this.updateActiveFilters();
    
    // Sort and render filtered products (this will also apply pagination)
    this.sortProducts();
    
    // Show/hide clear filters button
    const hasActiveFilters = Object.values(this.activeFilters).some(arr => arr.length > 0);
    this.clearFiltersBtn.forEach(btn => {
      if (hasActiveFilters) {
        btn.removeAttribute('hidden');
      } else {
        btn.setAttribute('hidden', '');
      }
    });
  }

  clearAllFilters() {
    // Uncheck all filter inputs
    this.filterInputs.forEach(input => {
      input.checked = false;
    });
    
    // Reset active filters
    this.activeFilters = {
      make: [],
      condition: [],
      price: [],
      category: []
    };
    
    // Reset filtered products
    this.filteredProducts = [...this.products];
    
    // Update display
    this.updateActiveFilters();
    this.sortProducts(); // This will apply pagination automatically
    
    // Hide clear filters button
    this.clearFiltersBtn.forEach(btn => {
      btn.setAttribute('hidden', '');
    });
  }

  updateActiveFilters() {
    if (!this.activeFiltersList) return;
    
    const hasFilters = Object.values(this.activeFilters).some(arr => arr.length > 0);
    
    if (!hasFilters) {
      this.activeFiltersContainer.style.display = 'none';
      return;
    }
    
    this.activeFiltersContainer.style.display = 'block';
    
    // Build filter tags
    let filtersHTML = '';
    
    Object.entries(this.activeFilters).forEach(([type, values]) => {
      values.forEach(value => {
        const label = this.getFilterLabel(type, value);
        filtersHTML += `
          <button class="active-filter-tag" data-remove-filter="${type}:${value}">
            ${label}
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M12 4L4 12M4 4l8 8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
            </svg>
          </button>
        `;
      });
    });
    
    this.activeFiltersList.innerHTML = filtersHTML;
    
    // Add event listeners to remove filter buttons
    this.activeFiltersList.querySelectorAll('[data-remove-filter]').forEach(btn => {
      btn.addEventListener('click', () => {
        const [type, value] = btn.dataset.removeFilter.split(':');
        this.removeFilter(type, value);
      });
    });
  }

  getFilterLabel(type, value) {
    if (type === 'make') {
      return value.charAt(0).toUpperCase() + value.slice(1);
    } else if (type === 'condition') {
      return value.replace('condition-', '').charAt(0).toUpperCase() + 
             value.replace('condition-', '').slice(1);
    } else if (type === 'category') {
      return value.replace('category-', '').replace(/-/g, ' ')
        .split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
    } else if (type === 'price') {
      if (value === '500+') return '$500+';
      const [min, max] = value.split('-');
      return `$${min} - $${max}`;
    }
    return value;
  }

  removeFilter(type, value) {
    // Find and uncheck the input
    const input = Array.from(this.filterInputs).find(inp => 
      inp.dataset.filter === type && inp.value === value
    );
    
    if (input) {
      input.checked = false;
    }
    
    // Reapply filters
    this.applyFilters();
  }

  applyPagination() {
    if (!this.productsContainer) return;

    // Get current page from URL
    const urlParams = new URLSearchParams(window.location.search);
    const currentPage = parseInt(urlParams.get('page') || '1', 10);

    // Server-side pagination: server sends only the current page of products (e.g. 50 items).
    // If we have at most one page of products in DOM, show all of them.
    const isSinglePageFromServer = this.filteredProducts.length <= this.currentPerPage;

    if (isSinglePageFromServer) {
      // One page of products in DOM – show all (no client-side slicing)
      this.filteredProducts.forEach(product => {
        product.element.style.display = 'block';
        product.element.style.visibility = 'visible';
      });
    } else {
      // Client-side pagination: multiple pages in DOM, show only current page slice
      const totalPages = Math.max(1, Math.ceil(this.filteredProducts.length / this.currentPerPage));
      const startIndex = (currentPage - 1) * this.currentPerPage;
      const endIndex = Math.min(startIndex + this.currentPerPage, this.filteredProducts.length);
      const productsToShow = this.filteredProducts.slice(startIndex, endIndex);

      this.filteredProducts.forEach(product => {
        product.element.style.display = 'none';
        product.element.style.visibility = 'hidden';
      });
      productsToShow.forEach(product => {
        product.element.style.display = 'block';
        product.element.style.visibility = 'visible';
      });
    }

    // Hide any non-filtered products
    if (this.productsContainer) {
      const allProducts = Array.from(this.productsContainer.querySelectorAll('[data-product-item]'));
      allProducts.forEach(item => {
        const isFiltered = this.filteredProducts.some(p => p.element === item);
        if (!isFiltered) {
          item.style.display = 'none';
          item.style.visibility = 'hidden';
        }
      });
    }

    // Ensure container is visible after initial render (avoid flash)
    if (this.isInitialRender) {
      this.isInitialRender = false;
      this.productsContainer.style.opacity = '1';
    }

    // Update pagination info
    this.updateProductCount();
  }

  renderPagination(totalPages, currentPage) {
    // No-op when using native Liquid pagination.
    return;
  }

  goToPage(page) {
    // With native Liquid pagination, page links are normal anchors.
    // This method is kept for backward compatibility but not used.
    return;
  }

  renderProducts(products) {
    if (products.length === 0) {
      this.productsContainer.style.display = 'none';
      if (this.noResults) {
        this.noResults.style.display = 'flex';
      }
      return;
    }
    
    this.productsContainer.style.display = 'grid';
    if (this.noResults) {
      this.noResults.style.display = 'none';
    }
    
    // Products are already in DOM from sortProducts(), just apply pagination
    this.applyPagination();
  }

  updateProductCount() {
    // Update product count element if it exists
    if (this.productCount) {
      this.productCount.textContent = this.filteredProducts.length;
    }

    // Update pagination info text (use server total when server-side pagination)
    const paginationInfo = document.getElementById('pagination-info');
    if (paginationInfo) {
      const urlParams = new URLSearchParams(window.location.search);
      const currentPage = parseInt(urlParams.get('page') || '1', 10);
      const paginationContainer = document.querySelector('[data-pagination-total]');
      const serverTotal = paginationContainer ? parseInt(paginationContainer.dataset.totalProducts || '0', 10) : 0;
      const isServerPagination = serverTotal > 0 && this.filteredProducts.length <= this.currentPerPage;

      const totalItems = isServerPagination ? serverTotal : this.filteredProducts.length;
      const startIndex = (currentPage - 1) * this.currentPerPage;
      const endIndex = Math.min(startIndex + this.currentPerPage, totalItems);

      paginationInfo.textContent = `Showing ${startIndex + 1}‑${endIndex} of ${totalItems} parts`;
    }
  }

  changeView(view) {
    this.currentView = view;
    
    // Update button states
    this.viewToggleBtns.forEach(btn => {
      if (btn.dataset.view === view) {
        btn.classList.add('view-toggle__btn--active');
      } else {
        btn.classList.remove('view-toggle__btn--active');
      }
    });
    
    // Update grid class
    if (this.productsContainer) {
      if (view === 'grid') {
        this.productsContainer.classList.remove('collection-products__grid--list');
        this.productsContainer.classList.add('collection-products__grid--grid');
      } else {
        this.productsContainer.classList.remove('collection-products__grid--grid');
        this.productsContainer.classList.add('collection-products__grid--list');
      }
    }
  }

  openMobileFilters() {
    if (!this.filtersOverlay) return;
    
    // Clone filters to overlay on first open
    const overlayContent = this.filtersOverlay.querySelector('.collection-filters-overlay__content');
    if (overlayContent && overlayContent.children.length === 0) {
      const filtersClone = document.querySelector('.collection-filters').cloneNode(true);
      overlayContent.appendChild(filtersClone);
      
      // Re-attach event listeners to cloned inputs
      overlayContent.querySelectorAll('[data-filter]').forEach(input => {
        input.addEventListener('change', () => {
          // Sync with main filters
          const mainInput = Array.from(this.filterInputs).find(inp => 
            inp.dataset.filter === input.dataset.filter && inp.value === input.value
          );
          if (mainInput) {
            mainInput.checked = input.checked;
          }
          this.applyFilters();
        });
      });
    }
    
    this.filtersOverlay.classList.add('is-open');
    document.body.style.overflow = 'hidden';
  }

  closeMobileFilters() {
    if (!this.filtersOverlay) return;
    
    this.filtersOverlay.classList.remove('is-open');
    document.body.style.overflow = '';
  }

  updateURL() {
    const url = new URL(window.location.href);
    url.searchParams.set('sort_by', this.currentSort);
    // When sort changes, reset to page 1 and let server handle pagination
    url.searchParams.delete('page');
    window.location.href = url.toString();
  }

  // New methods for search, columns, and per page
  handleSearch(searchTerm) {
    this.searchTerm = searchTerm ? searchTerm.trim().toLowerCase() : '';
    this.applyFilters();
  }

  handleColumnChange(columns) {
    this.currentColumns = parseInt(columns);
    
    // Update button states
    this.columnBtns.forEach(btn => {
      if (btn.dataset.columns === columns) {
        btn.classList.add('column-btn--active');
      } else {
        btn.classList.remove('column-btn--active');
      }
    });
    
    // Update grid columns
    if (this.productsContainer) {
      this.productsContainer.className = `collection-products__grid collection-products__grid--grid collection-products__grid--${columns}-cols`;
    }
  }

  handlePerPageChange(perPage) {
    const url = new URL(window.location.href);
    url.searchParams.set('limit', perPage);
    // Reset to page 1 when page size changes and let server handle pagination
    url.searchParams.delete('page');
    window.location.href = url.toString();
  }

  // Debounce utility function
  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }
}

// Initialize on DOM ready
let collectionHandler = null;

function initializeCollectionHandler() {
  // Check if products container exists
  const productsContainer = document.querySelector('[data-products-container]');
  if (!productsContainer) {
    // Retry if container not found yet
    setTimeout(initializeCollectionHandler, 100);
    return;
  }
  
  // Check if products are loaded
  const productItems = productsContainer.querySelectorAll('[data-product-item]');
  if (productItems.length === 0) {
    // Retry if no products found yet
    setTimeout(initializeCollectionHandler, 100);
    return;
  }
  
  // Initialize handler
  if (!collectionHandler) {
    collectionHandler = new CollectionHandler();
    
    // CRITICAL: After initialization, ensure sort is applied
    // This handles cases where setSortFromURL() might not have triggered sorting
    setTimeout(() => {
      if (collectionHandler) {
        const urlParams = new URLSearchParams(window.location.search);
        const sortParam = urlParams.get('sort_by');
        
        // If there's a sort parameter, ensure it's applied
        if (sortParam && collectionHandler.filteredProducts.length > 0) {
          // Double-check that sort is applied
          if (collectionHandler.currentSort === sortParam) {
            // Sort is set correctly, ensure it's applied
            collectionHandler.sortProducts();
          } else {
            // Sort value doesn't match, re-apply
            collectionHandler.setSortFromURL();
          }
        } else if (collectionHandler.filteredProducts.length > 0) {
          // No sort param but products exist, apply current sort
          collectionHandler.sortProducts();
        }
      }
    }, 200);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    initializeCollectionHandler();
  });
} else {
  initializeCollectionHandler();
}

// Also try on window load as fallback
window.addEventListener('load', () => {
  setTimeout(() => {
    if (!collectionHandler) {
      initializeCollectionHandler();
    } else {
      // Re-apply sort if handler already exists (for page navigation)
      // This is critical when navigating between pages via direct URL
      const urlParams = new URLSearchParams(window.location.search);
      const sortParam = urlParams.get('sort_by');
      
      // Re-store products in case page was reloaded
      if (collectionHandler.productsContainer) {
        collectionHandler.storeProducts();
      }
      
      if (sortParam && collectionHandler.sortSelect) {
        collectionHandler.sortSelect.value = sortParam;
        collectionHandler.currentSort = sortParam;
      }
      
      // Always apply sorting on window load (for page navigation)
      // Use forceApplySort to ensure it works
      collectionHandler.forceApplySort();
    }
  }, 300);
  
  // Additional safety check after longer delay (catches slow-loading pages)
  setTimeout(() => {
    if (collectionHandler) {
      const urlParams = new URLSearchParams(window.location.search);
      const sortParam = urlParams.get('sort_by');
      
      // If sort parameter exists, ensure it's applied
      if (sortParam) {
        // Check if products are actually sorted correctly
        // If not, force re-apply
        if (collectionHandler.filteredProducts.length > 0) {
          collectionHandler.forceApplySort();
        }
      }
    }
  }, 800);
});

